import React from 'react';
import { useHistory } from 'react-router-dom';
import MI from '../../endpoints/market-intelligence';
import { useDataInsights } from './insights';
import { PageHeader, Ribbon, Card, Empty, Pending } from '../../components/analytics-kit';
import { computeRangeLabel } from './range-options';
import { ribbonTiles, sumPlaces, GEO_NOTE, ChannelsCard, MarketsCard, TopProductsCard, TopCategoriesCard, ZipsCard, AnalysisCard } from './kit-sections';
import useAgentRun, { extractSummary } from './use-agent-run';
import AiDrawer from './ai-drawer';
import useReportData from './use-report-data';
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from '../../functions/period-store';

// MI's comparison vocabulary differs from the global store's.
const miComp = c => c === 'prev_year' ? 'previous_year' : 'previous_period';
import { normalizeLocations } from './normalize';
import { SegmentCampaignsTable } from './sections';
import { MARKETS, ZIP_SCOPED_MARKET, CAMPAIGN_ONLY_MARKETS, DERIVE_MARKETS_FROM_LOCATIONS, SEGMENT_MARKETS } from './report-config';
import { clusterRows } from './aggregations';
import GeoMaps from './geo-maps';
import AllMarketsMap from './all-markets-map';

// Market Intelligence — report-style page mirroring the CLI's monthly
// performance report (Plunk.Reporting/Rendering/MarkdownReportRenderer.cs).
// Dropdown selects "All Markets" (cluster view) or a specific market.

const CLUSTER_KEY = '__cluster__';

// Client-side memory of the user's last completed analysis per market/range/
// comparison, so we can offer a "Last analysis · View" chip (no cross-user
// backend endpoint exists yet — this is per-browser). Value = {id, at}.
function miRunKey(locationId, range, comparison) {
  return locationId ? `mi-last-run:${locationId}:${range}:${comparison}` : null;
}
function fmtDate(ms) {
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  } catch (e) {
    return '';
  }
}
function useLocations() {
  const [state, setState] = React.useState({
    loading: true,
    error: null,
    locations: []
  });
  React.useEffect(() => {
    let cancelled = false;
    MI.getLocations().then(raw => {
      if (cancelled) return;
      setState({
        loading: false,
        error: null,
        locations: normalizeLocations(raw)
      });
    }).catch(err => {
      if (cancelled) return;
      setState({
        loading: false,
        error: err.message || String(err),
        locations: []
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}
export function MarketIntelligenceView(props) {
  const history = useHistory();
  const initialRange = props.params && props.params.range || getGlobalRange();
  const [range, setRange] = React.useState(initialRange);
  const [comparison, setComparison] = React.useState(miComp(getGlobalComp()));
  // ?location=<market name> deep-links a market (Web Activity › Places › Markets); unknown names fall back to the cluster view.
  const [location, setLocation] = React.useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("location");
      return q || CLUSTER_KEY;
    } catch (e) {
      return CLUSTER_KEY;
    }
  });

  // Follow the global top-bar period control (range + comparison).
  React.useEffect(() => {
    const tok = subscribePeriod(({
      range: r,
      comp
    }) => {
      if (r) setRange(r);
      if (comp) setComparison(miComp(comp));
    });
    return () => unsubscribePeriod(tok);
  }, []);
  const {
    loading: locLoading,
    error: locError,
    locations
  } = useLocations();

  // Markets: R&S keeps the bespoke static report structure; every other
  // tenant derives its market list from its OWN MI locations (2026-07-14 —
  // Fairfield's data existed but the static [] gate hid it).
  const markets = React.useMemo(() => {
    if (!DERIVE_MARKETS_FROM_LOCATIONS) return MARKETS;
    return (locations || []).map(l => l.name || l.LocationName || '').filter(Boolean);
  }, [locations]);
  const report = useReportData({
    range,
    comparison,
    markets
  });
  const orchestrator = useAgentRun();
  // Data insights ride the same Run AI Analysis click (drawer section); no separate buttons.
  const insights = useDataInsights();
  const [aiOpen, setAiOpen] = React.useState(false);
  const [lastSaved, setLastSaved] = React.useState(null); // {id, at} for the "Last analysis" chip

  React.useEffect(() => {
    orchestrator.reset();
    setAiOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, range, comparison]);

  // Full AI report from the orchestrator run
  const fullReport = orchestrator.result ? orchestrator.result.fullReport || orchestrator.result.FullReport || extractSummary(orchestrator.result) : null;

  // Resolve location id for the AI agent (per-market only)
  const locationId = React.useMemo(() => {
    if (location === CLUSTER_KEY) return null;
    const match = (locations || []).find(l => (l.name || l.LocationName || String(l)) === location);
    if (!match) return location;
    return match.locationId || match.LocationId || match.id || match.Id || location;
  }, [locations, location]);
  const handleRunAnalysis = React.useCallback(() => {
    if (!location || location === CLUSTER_KEY) return;
    setAiOpen(true);
    orchestrator.start({
      agent: 'market_intelligence_orchestrator',
      locationId,
      range,
      comparisonType: comparison
    });
    insights.generateAll(range); // in parallel; cached (<24h) types return instantly
  }, [location, locationId, range, comparison, orchestrator, insights]);

  // Pointer to the last analysis for this market (for the chip) — does NOT
  // auto-open the drawer. Seed from this browser's localStorage immediately,
  // then upgrade to the tenant-wide latest completed run from the backend so
  // the chip works for ANY user, not just whoever ran it in this browser.
  React.useEffect(() => {
    let cancelled = false;
    const key = miRunKey(locationId, range, comparison);
    let local = null;
    try {
      local = key ? JSON.parse(localStorage.getItem(key) || 'null') : null;
    } catch (e) {/* private mode */}
    setLastSaved(local && local.id ? local : null);
    if (locationId && location !== CLUSTER_KEY) {
      MI.getLatestRun({
        agent: 'market_intelligence_orchestrator',
        locationId
      }).then(run => {
        if (cancelled || !run) return;
        const id = run.Id || run.id;
        const at = run.ModifiedDate || run.modifiedDate || run.CreatedDate || run.createdDate;
        if (id) setLastSaved({
          id: String(id),
          at: at ? new Date(at).getTime() : Date.now()
        });
      }).catch(() => {/* keep the localStorage fallback */});
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, range, comparison]);

  // Persist a NEW completed run (not a re-loaded one, so the stored date stays
  // the run's real completion, not the view time).
  React.useEffect(() => {
    if (!orchestrator.result || !orchestrator.runId || orchestrator.error) return;
    const key = miRunKey(locationId, range, comparison);
    if (!key) return;
    try {
      const existing = JSON.parse(localStorage.getItem(key) || 'null');
      if (existing && String(existing.id) === String(orchestrator.runId)) return; // already stored — keep original date
      const entry = {
        id: String(orchestrator.runId),
        at: Date.now()
      };
      localStorage.setItem(key, JSON.stringify(entry));
      setLastSaved(entry);
    } catch (e) {/* private mode */}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orchestrator.result, orchestrator.runId]);
  const openLastAnalysis = React.useCallback(() => {
    if (!lastSaved) return;
    setAiOpen(true);
    orchestrator.loadExisting(lastSaved.id);
    insights.load(range); // stored summaries only — no provider calls when just viewing
  }, [lastSaved, orchestrator, range, insights]);
  const isCluster = location === CLUSTER_KEY;

  // All-markets campaign rows in (so the cluster Snapshot + Combined
  // tables can render). We need every market's `rows` to have come back —
  // any still loading means the cluster totals would be partial.
  const allCampaignsLoaded = markets.every(m => report.marketData[m] && !report.marketData[m].rowsLoading);

  // Cluster campaign rows (excludes Wellen Park slice to prevent double-count)
  const clusterCampaignRows = React.useMemo(() => {
    const byMarket = {};
    for (const m of markets) {
      byMarket[m] = report.marketData[m] && report.marketData[m].rows || [];
    }
    return clusterRows(byMarket);
  }, [report.marketData]);
  const prevClusterCampaignRows = React.useMemo(() => {
    const byMarket = {};
    for (const m of markets) {
      byMarket[m] = report.marketData[m] && report.marketData[m].prevRows || [];
    }
    return clusterRows(byMarket);
  }, [report.marketData]);
  const allPrevCampaignsLoaded = markets.every(m => report.marketData[m] && !report.marketData[m].prevRowsLoading);
  const clusterReady = allCampaignsLoaded && !report.placesLoading;
  const clusterPrevReady = allPrevCampaignsLoaded && !report.prevPlacesLoading;
  const rangeLabel = computeRangeLabel(range, comparison);
  const comparisonLabel = comparison === 'previous_year' ? 'vs prior year' : 'vs previous period';
  const pick = loc => {
    setLocation(loc);
    try {
      history.replace(`/MarketIntelligence${loc === CLUSTER_KEY ? '' : `?location=${encodeURIComponent(loc)}`}`);
    } catch (e) {/* no router */}
  };
  const entry = !isCluster ? report.marketData[location] || {} : null;
  const clusterWeb = {
    views: sumPlaces(report.placeIndex, 'views'),
    sessions: sumPlaces(report.placeIndex, 'sessions'),
    uniqueUsers: sumPlaces(report.placeIndex, 'uniqueUsers'),
    conversions: sumPlaces(report.placeIndex, 'conversions'),
    avgTimeSpan: 0
  };
  const clusterPrevWeb = {
    views: sumPlaces(report.prevPlaceIndex, 'views'),
    sessions: sumPlaces(report.prevPlaceIndex, 'sessions'),
    uniqueUsers: sumPlaces(report.prevPlaceIndex, 'uniqueUsers'),
    conversions: sumPlaces(report.prevPlaceIndex, 'conversions')
  };
  const primaryBtn = 'k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary';
  const baseBtn = 'k-button k-button-md k-rounded-md k-button-solid k-button-solid-base';
  const runButton = /*#__PURE__*/React.createElement("button", {
    className: primaryBtn,
    onClick: handleRunAnalysis,
    disabled: orchestrator.running || isCluster,
    title: isCluster ? 'AI analysis runs per market: pick a market first' : undefined
  }, orchestrator.running ? 'Analyzing…' : 'Run AI analysis');
  return /*#__PURE__*/React.createElement("div", {
    className: "territory-view wa-view mi-view"
  }, /*#__PURE__*/React.createElement(PageHeader, {
    crumbs: isCluster ? null : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: 'pointer'
      },
      onClick: () => pick(CLUSTER_KEY)
    }, "Market Intelligence"), " \xB7 Market"),
    title: isCluster ? 'Market Intelligence' : location,
    subtitle: `${rangeLabel || ''}${rangeLabel ? ' · ' : ''}${comparisonLabel} · paid delivery and geo-targeted site traffic per market`,
    chips: [markets.length ? {
      k: 'Markets',
      v: String(markets.length)
    } : null, !isCluster && lastSaved ? {
      k: 'Last analysis',
      v: fmtDate(lastSaved.at)
    } : null],
    actions: /*#__PURE__*/React.createElement(React.Fragment, null, DERIVE_MARKETS_FROM_LOCATIONS ? /*#__PURE__*/React.createElement("button", {
      className: baseBtn,
      onClick: () => history.push('/Locations?add=1&return=mi'),
      title: "Create a market in Manage Data: it becomes trackable here and targetable in your next flight"
    }, "Add market") : null, !isCluster && lastSaved && !orchestrator.result && !orchestrator.running ? /*#__PURE__*/React.createElement("button", {
      className: baseBtn,
      onClick: openLastAnalysis
    }, "View last analysis") : null, runButton)
  }), markets.length > 0 ? /*#__PURE__*/React.createElement("nav", {
    className: "wa-switch",
    "aria-label": "Markets"
  }, /*#__PURE__*/React.createElement("a", {
    className: 'wa-tab' + (isCluster ? ' on' : ''),
    style: {
      cursor: 'pointer'
    },
    onClick: () => pick(CLUSTER_KEY)
  }, "All markets"), markets.map(m => /*#__PURE__*/React.createElement("a", {
    key: m,
    className: 'wa-tab' + (location === m ? ' on' : ''),
    style: {
      cursor: 'pointer'
    },
    onClick: () => pick(m)
  }, m))) : null, locError ? /*#__PURE__*/React.createElement("div", {
    className: "wa-notice danger"
  }, "Could not load locations: ", locError) : null, report.error ? /*#__PURE__*/React.createElement("div", {
    className: "wa-notice danger"
  }, report.error) : null, markets.length === 0 && !locLoading ? /*#__PURE__*/React.createElement(Empty, null, DERIVE_MARKETS_FROM_LOCATIONS ? 'No markets yet. Add one under Manage Data › Locations and its campaigns and geo-targeted traffic report here.' : 'No Market Intelligence report is configured for this account yet.') : null, markets.length > 0 && isCluster ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Ribbon, {
    loading: !clusterReady,
    comparing: clusterPrevReady ? true : undefined,
    tiles: ribbonTiles({
      rows: clusterCampaignRows,
      prevRows: clusterPrevReady ? prevClusterCampaignRows : null,
      web: clusterWeb,
      prevWeb: clusterPrevReady ? clusterPrevWeb : null
    }),
    note: GEO_NOTE
  }), /*#__PURE__*/React.createElement("div", {
    className: "wa-grid"
  }, /*#__PURE__*/React.createElement("div", {
    className: "wa-cell c8"
  }, /*#__PURE__*/React.createElement(AllMarketsMap, {
    markets: markets,
    locations: locations,
    placeIndex: report.placeIndex,
    marketData: report.marketData,
    onOpenMarket: pick
  })), /*#__PURE__*/React.createElement(MarketsCard, {
    markets: markets,
    placeIndex: report.placeIndex,
    prevPlaceIndex: report.prevPlaceIndex,
    marketData: report.marketData,
    prevReady: clusterPrevReady,
    onOpen: pick,
    span: 4
  }), /*#__PURE__*/React.createElement(ChannelsCard, {
    rows: clusterCampaignRows,
    loading: !allCampaignsLoaded,
    title: "Campaigns by channel",
    span: 6
  }), /*#__PURE__*/React.createElement(TopProductsCard, {
    rows: report.clusterTopProducts,
    loading: report.clusterPerfLoading,
    cluster: true,
    span: 6,
    hint: "all markets \xB7 by views"
  }))) : null, markets.length > 0 && !isCluster ? markets.includes(location) ? /*#__PURE__*/React.createElement(MarketView, {
    market: location,
    entry: entry,
    report: report,
    range: range,
    onRun: runButton
  }) : /*#__PURE__*/React.createElement(Empty, null, "No report data for ", location, ". Pick one of the markets above.") : null, /*#__PURE__*/React.createElement(AiDrawer, {
    open: aiOpen,
    onClose: () => setAiOpen(false),
    report: fullReport,
    running: orchestrator.running,
    market: isCluster ? 'All Markets' : location,
    range: range,
    insights: insights,
    onRefreshInsights: force => insights.generateAll(range, force)
  }));
}
function MarketView({
  market,
  entry,
  report,
  range,
  onRun
}) {
  const m = entry || {};
  const isZipScoped = market === ZIP_SCOPED_MARKET;
  const isCampaignOnly = CAMPAIGN_ONLY_MARKETS.has(market);
  const derivedReady = !m.placeZipsLoading && !report.productsLoading;
  const ribbonLoading = m.rowsLoading || m.placeZipsLoading;
  const comparing = m.prevRowsLoading ? undefined : m.prevPlace || (m.prevRows || []).length ? true : false;
  return /*#__PURE__*/React.createElement(React.Fragment, null, isZipScoped ? /*#__PURE__*/React.createElement("div", {
    className: "wa-notice"
  }, market, " is a zip-targeted slice of Sarasota's campaigns. Its figures are already inside Sarasota's totals and the all-markets roll-up; it is shown so you can see how this zip performs inside the broader footprint.") : null, /*#__PURE__*/React.createElement(Ribbon, {
    loading: ribbonLoading,
    comparing: comparing,
    tiles: ribbonTiles({
      rows: m.rows,
      prevRows: m.prevRowsLoading ? null : m.prevRows,
      web: m.place,
      prevWeb: m.prevPlace
    }),
    note: isCampaignOnly ? null : GEO_NOTE
  }), /*#__PURE__*/React.createElement("div", {
    className: "wa-grid"
  }, !isZipScoped ? /*#__PURE__*/React.createElement(ChannelsCard, {
    rows: m.rows,
    loading: m.rowsLoading,
    market: market,
    span: isCampaignOnly ? 12 : 6
  }) : null, !isCampaignOnly ? /*#__PURE__*/React.createElement(ZipsCard, {
    placeZips: m.placeZips,
    cityZips: m.cityZips,
    loading: m.placeZipsLoading || m.cityZipsLoading,
    marketName: market,
    span: isZipScoped ? 12 : 6
  }) : null, !isZipScoped && !m.rowsLoading && SEGMENT_MARKETS.has(market) ? /*#__PURE__*/React.createElement("div", {
    className: "wa-cell c12"
  }, /*#__PURE__*/React.createElement(SegmentCampaignsTable, {
    market: market,
    rows: m.rows
  })) : null, !isCampaignOnly && !isZipScoped ? /*#__PURE__*/React.createElement("div", {
    className: "wa-cell c12"
  }, /*#__PURE__*/React.createElement(GeoMaps, {
    market: market,
    range: range,
    hideTable: true
  })) : null, !isCampaignOnly ? /*#__PURE__*/React.createElement(TopProductsCard, {
    rows: m.topProducts,
    loading: !derivedReady,
    span: 6,
    hint: "by views in this footprint"
  }) : null, !isCampaignOnly ? /*#__PURE__*/React.createElement(TopCategoriesCard, {
    rows: m.topCategories,
    loading: !derivedReady,
    span: 6
  }) : null, !m.rowsLoading && derivedReady ? /*#__PURE__*/React.createElement(AnalysisCard, {
    market: market,
    marketEntry: m,
    allMarketData: report.marketData,
    right: onRun,
    span: 12
  }) : null));
}
export default MarketIntelligenceView;